import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about" },
    { label: "Services", path: "/services" },
    { label: "Care Plans", path: "/care-plans" },
    { label: "Pricing", path: "/pricing" },
    { label: "Reviews", path: "/reviews" },
    { label: "FAQ", path: "/faq" },
    { label: "Service Areas", path: "/service-areas" },
    { label: "Careers", path: "/careers" },
    { label: "Contact Us", path: "/contact" },
  ];

  const dashboardPath = user?.role === "admin" ? "/dashboard/admin" : "/dashboard/client";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-sm shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <img
                src="https://static.readdy.ai/image/2f07940fa5d3a90c9fe64cfd1f3be31d/a5100fd298f183af8516edaf12c4c388.png"
                alt="Sycamore Oasis Corp Logo"
                className="h-10 md:h-12 w-auto object-contain"
              />
              <div className="hidden sm:block">
                <span
                  className={`text-base md:text-lg font-semibold leading-tight transition-colors duration-300 ${
                    isScrolled ? "text-brand-green-800" : "text-white"
                  }`}
                >
                  Sycamore Oasis
                </span>
                <span
                  className={`block text-[10px] md:text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isScrolled ? "text-brand-green-600" : "text-white/80"
                  }`}
                >
                  Home Care Services
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-2 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === link.path
                      ? isScrolled
                        ? "text-brand-green-700 bg-brand-green-50"
                        : "text-white bg-white/15"
                      : isScrolled
                        ? "text-gray-600 hover:text-brand-green-700 hover:bg-gray-50"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
              {user ? (
                <>
                  <Link
                    to={dashboardPath}
                    className={`px-3 xl:px-5 py-2 text-xs xl:text-sm font-semibold rounded-full border transition-all duration-200 whitespace-nowrap ${
                      isScrolled
                        ? "border-brand-green-600 text-brand-green-600 hover:bg-brand-green-50"
                        : "border-white text-white hover:bg-white/10"
                    }`}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={signOut}
                    className={`px-3 xl:px-5 py-2 text-xs xl:text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap ${
                      isScrolled
                        ? "bg-red-50 text-red-600 hover:bg-red-100"
                        : "bg-white/15 text-white hover:bg-white/25"
                    }`}
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/contact"
                  className={`px-3 xl:px-5 py-2 text-xs xl:text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap ${
                    isScrolled
                      ? "bg-brand-green-600 text-white hover:bg-brand-green-700"
                      : "bg-white text-brand-green-700 hover:bg-white/90"
                  }`}
                >
                  Get Care Now
                </Link>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 rounded-md"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i
                className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl ${
                  isScrolled ? "text-brand-green-800" : "text-white"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ${
            isMobileMenuOpen ? "visible" : "invisible"
          }`}
        >
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
              isMobileMenuOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Menu Panel */}
          <div
            className={`absolute top-16 left-0 right-0 bg-white shadow-lg transition-all duration-300 overflow-hidden ${
              isMobileMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === link.path
                      ? "text-brand-green-700 bg-brand-green-50"
                      : "text-gray-700 hover:text-brand-green-700 hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link
                      to={dashboardPath}
                      className="block text-center px-4 py-2.5 text-sm font-semibold rounded-full border border-brand-green-600 text-brand-green-600 hover:bg-brand-green-50 whitespace-nowrap"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => { signOut(); setIsMobileMenuOpen(false); }}
                      className="block text-center px-4 py-2.5 text-sm font-semibold rounded-full bg-red-50 text-red-600 hover:bg-red-100 whitespace-nowrap"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/contact"
                    className="block text-center px-4 py-2.5 text-sm font-semibold rounded-full bg-brand-green-600 text-white hover:bg-brand-green-700 whitespace-nowrap"
                  >
                    Get Care Now
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}